import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { safeSessionStorage } from '@/lib/storage';
import type { VehicleSelection } from '@/types/domain';
import {
  addInterest as addInterestState,
  EMPTY_JOURNEY,
  parseJourneyJson,
  removeInterest as removeInterestState,
  serializeJourneyState,
  withService as withServiceState,
  withVehicle as withVehicleState,
  type InterestSource,
  type JourneyState,
} from './journeyState';

const STORAGE_KEY = 'aqs.journey';

export interface JourneyValue {
  state: JourneyState;
  setVehicle: (vehicle: VehicleSelection) => void;
  setService: (serviceId: string | null) => void;
  addInterest: (code: string, source: InterestSource) => void;
  removeInterest: (code: string) => void;
  /** Clears the whole journey (used after a booking is created). */
  reset: () => void;
}

const JourneyContext = createContext<JourneyValue | null>(null);

/**
 * Shared customer journey (vehicle, explicitly selected product interests,
 * chosen service): survives navigation across the public pages AND the
 * sign-in detour, because it is mounted above the router and mirrored to
 * sessionStorage (cleared when the browser tab closes — this is a shopping
 * aid, not an account record). Only non-sensitive vehicle-master ids and
 * catalogue codes are ever stored (see journeyState.ts).
 */
export function JourneyProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<JourneyState>(() => parseJourneyJson(safeSessionStorage.get(STORAGE_KEY)));

  useEffect(() => {
    if (state === EMPTY_JOURNEY) safeSessionStorage.remove(STORAGE_KEY);
    else safeSessionStorage.set(STORAGE_KEY, serializeJourneyState(state));
  }, [state]);

  const setVehicle = useCallback((vehicle: VehicleSelection) => setState((s) => withVehicleState(s, vehicle)), []);
  const setService = useCallback((serviceId: string | null) => setState((s) => withServiceState(s, serviceId)), []);
  const addInterest = useCallback((code: string, source: InterestSource) => setState((s) => addInterestState(s, code, source)), []);
  const removeInterest = useCallback((code: string) => setState((s) => removeInterestState(s, code)), []);
  const reset = useCallback(() => setState(EMPTY_JOURNEY), []);

  const value = useMemo<JourneyValue>(
    () => ({ state, setVehicle, setService, addInterest, removeInterest, reset }),
    [state, setVehicle, setService, addInterest, removeInterest, reset],
  );

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

export function useJourney(): JourneyValue {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error('useJourney must be used inside <JourneyProvider>');
  return ctx;
}
